import { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Globe, Info } from 'lucide-react';
import { TimeSlots } from '@/components/time-slots';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { detectTimeZone } from '@/lib/timezone';

interface DateTimeSelectorProps {
  username: string;
  eventTypeSlug: string;
  onTimeSelected: (date: Date, startTime: string, endTime: string) => void;
  initialTimezone?: string;
}

export function DateTimeSelector({
  username,
  eventTypeSlug,
  onTimeSelected,
  initialTimezone,
}: DateTimeSelectorProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    start: string;
    end: string;
  } | null>(null);
  const [timezone, setTimezone] = useState<string>(
    initialTimezone || detectTimeZone()
  );

  // Get current date for default calendar view
  const today = new Date();
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const handleTimeSelect = (startTime: string, endTime: string) => {
    setSelectedTimeSlot({ start: startTime, end: endTime });
    if (selectedDate) {
      onTimeSelected(selectedDate, startTime, endTime);
    }
  };

  return (
    <div className="space-y-6 mx-auto md:mx-0 max-w-md w-full">
      <Card className="overflow-hidden border shadow-md">
        <div className="p-4 border-b bg-muted">
          <h2 className="text-lg font-semibold text-foreground text-center">
            Select Date & Time
          </h2>
        </div>

        <CardContent className="p-4 space-y-6">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-primary" />
              <label
                htmlFor="timezone-select"
                className="text-sm font-medium text-foreground"
              >
                Timezone
              </label>
            </div>
            <TimezoneSelect
              value={timezone}
              onValueChange={(newTimezone) => {
                setTimezone(newTimezone);
                // Reset selected date and time slot when timezone changes
                setSelectedDate(undefined);
                setSelectedTimeSlot(null);
              }}
              placeholder="Select timezone"
            />
            <p className="text-xs text-muted-foreground">
              Times are shown in your selected timezone. Change this to see
              available times in another timezone.
            </p>
          </div>

          <div className="flex justify-center items-center border rounded-md p-2 bg-card">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                setSelectedDate(date);
                setSelectedTimeSlot(null);
              }}
              defaultMonth={currentMonth}
              className="mx-auto"
              disabled={(date) => {
                // Disable past dates
                return date < new Date(today.setHours(0, 0, 0, 0));
              }}
            />
          </div>

          {!selectedDate && (
            <div className="flex items-center justify-center p-2 text-muted-foreground text-sm">
              <Info className="h-4 w-4 mr-2" />
              <span>Select a date to view available time slots</span>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedDate && username && eventTypeSlug && (
        <TimeSlots
          date={selectedDate}
          onSelectTime={handleTimeSelect}
          selectedTime={selectedTimeSlot?.start || null}
          username={username}
          eventTypeSlug={eventTypeSlug}
          timezone={timezone}
        />
      )}

      {selectedDate && (!username || !eventTypeSlug) && (
        <div className="p-4 bg-destructive/10 rounded-lg text-center">
          <p className="text-destructive text-sm font-medium">
            Unable to load time slots: Missing required information
          </p>
          <p className="text-muted-foreground text-xs mt-1">
            Please check the URL or try again with valid information
          </p>
        </div>
      )}
    </div>
  );
}
